import { useState, useEffect } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { Button, Box, Chip } from "@mui/material";
import { useTheme } from "@emotion/react";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";

// Components
import { useNotification } from "../utils/Notification";
import Message from "../modals/Message";
import ConfirmationModal from "../utils/ConfirmationModal";
import {
  getLocalTimestamp,
  generateColorForChip,
  trackEvent,
} from "../utils/Utils";

// Theme
import { tokens } from "../../theme";

// Supabase
import supabase from "../../supabaseClient";

// Translation Keys
import { useTranslation } from "react-i18next";

export default function Failed() {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [messages, setMessages] = useState([]);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Context
  const { showNotification } = useNotification();

  const typeMapping = {
    me: t("upcoming.me"),
    group: t("upcoming.group"),
    bulk: t("upcoming.bulk"),
    contact: t("upcoming.contact"),
    manual: t("upcoming.manual"),
  };

  const messageStatusMapping = {
    failed: t("upcoming.failed"),
    partial_sent: t("upcoming.partial_failed"),
  };

  const columns = [
    {
      field: "type",
      headerName: t("failed.type"),
      width: 110,
      valueGetter: (params) => typeMapping[params.row.type] || params.row.type,
    },
    { field: "to", headerName: t("failed.to"), width: 200 },
    { field: "message", headerName: t("failed.message"), width: 300 },
    { field: "file", headerName: t("failed.file"), width: 200 },
    {
      field: "label",
      headerName: t("failed.label"),
      width: 150,
      renderCell: (params) => {
        if (!params.value) {
          return null;
        }

        const labelColor = generateColorForChip(params.value);
        return (
          <Chip
            label={params.value}
            color="primary"
            variant="outlined"
            sx={{
              borderColor: labelColor,
              color: labelColor,
              height: "20px",
            }}
          />
        );
      },
    },
    {
      field: "message_status",
      headerName: t("failed.status"),
      width: 120,
      renderCell: (params) => {
        const status = params.row.message_status;
        let chipColor = status === "failed" ? "red" : "darkOrange";

        return (
          <Chip
            label={
              messageStatusMapping[params.row.message_status] ||
              params.row.message_status
            }
            color="primary"
            variant="outlined"
            sx={{
              borderColor: chipColor,
              color: chipColor,
              height: "20px",
            }}
          />
        );
      },
    },
    { field: "error", headerName: t("failed.error"), width: 150 },
  ];

  useEffect(() => {
    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from("messages")
        .select(
          `
              id,
              to,
              contact_id (
                id,
                first_name,
                last_name,
                nickname
              ),
              group_id (
                id,
                name
              ),
              bulk_contacts,
              bulk_contacts_failed,
              name,
              countrycode_id,
              phone,
              message,
              file_id (
                id,
                name
              ),
              label_id (
                id,
                name
              ),
              message_frequency,
              message_status,
              scheduled_timestamp,
              end_timestamp,
              error
            `
        )
        .in("message_status", ["failed", "partial_sent"])
        .is("bulk_contacts_sent", null);

      if (error) {
        showNotification(
          `${t("failed.error_fetching_messages")}: ${error.message}`,
          "error"
        );
      } else {
        const formattedMessages = data.map((message) => ({
          id: message.id,
          type: message.to,
          to:
            message.to === "contact"
              ? `${message.contact_id.first_name} ${message.contact_id.last_name
              }${message.contact_id.nickname
                ? ` (${message.contact_id.nickname})`
                : ""
              }`
              : message.to === "group"
                ? message.group_id.name
                : message.to === "me"
                  ? t("upcoming.myself")
                  : message.name,
          message: message.message,
          file: message.file_id?.name,
          label: message.label_id?.name,
          frequency: message.message_frequency,
          message_status: message.message_status,
          next_at: getLocalTimestamp(message.scheduled_timestamp),
          last_at: getLocalTimestamp(message.end_timestamp),
          contact_id: message.contact_id?.id,
          group_id: message.group_id?.id,
          bulk_contacts: message.bulk_contacts,
          name: message.name,
          countrycode_id: message.countrycode_id,
          phone: message.phone,
          file_id: message.file_id?.id,
          label_id: message.label_id?.id,
          bulk_contacts_failed: message.bulk_contacts_failed,
          error: message.error,
        }));

        formattedMessages.sort(
          (a, b) => new Date(a.next_at) - new Date(b.next_at)
        );

        setMessages(formattedMessages);
      }
    };

    fetchMessages();
  }, [messageModalOpen, showDeleteConfirmation]);

  const handleExport = () => {
    trackEvent("failed-export_clicked");
    const csvData = messages.map((message) => ({
      Type: message.type,
      To: message.to,
      Message: message.message,
      File: message.file,
      Label: message.label,
      Frequency: message.frequency,
      Status: message.message_status,
      "Next At": message.next_at,
      "Last At": message.last_at,
      "Message Status": message.message_status,
    }));

    return csvData;
  };

  const handleCloseMessagesModal = () => {
    trackEvent("failed-message_modal_closed");
    setMessageModalOpen(false);
  };

  const handleRowClick = (row) => {
    trackEvent("failed-message_clicked");
    setSelectedMessage(row);
    setMessageModalOpen(true);
  };

  const handleRowSelection = (selection) => {
    trackEvent("failed-row_selected");
    setSelectedRows(selection);
  };

  const handleDeleteConfirmation = () => {
    trackEvent("failed-delete_clicked");
    setShowDeleteConfirmation(true);
  };

  const handleDeleteCancellation = () => {
    trackEvent("failed-delete_cancelled");
    setShowDeleteConfirmation(false);
  };

  const handleDelete = async () => {
    trackEvent("failed-delete_confirmed");
    try {
      const { error } = await supabase
        .from("messages")
        .delete()
        .in("id", selectedRows);

      if (error) {
        throw error;
      }

      setShowDeleteConfirmation(false);
      setSelectedRows([]);
    } catch (error) {
      showNotification(
        `${t("failed.error_deleting_messages")}: ${error.message}`,
        "error"
      );
    }
  };

  const retrySelected = async () => {
    trackEvent("failed-retry_clicked");
    try {
      const { error } = await supabase
        .from("messages")
        .update({
          message_status: "active",
          message_frequency: "once",
          error: null,
        })
        .in("id", selectedRows);

      if (error) {
        throw error;
      }

      setMessages((prevMessages) =>
        prevMessages.filter((message) => !selectedRows.includes(message.id))
      );

      setSelectedRows([]);
    } catch (error) {
      showNotification(
        `${t("failed.error_retrying_messages")}: ${error.message}`,
        "error"
      );
    }
  };

  return (
    <Box>
      <Box
        sx={{
          height: "auto",
          minHeight: "400px",
          width: "100%",
          "& .MuiDataGrid-root": {
            border: "none",
            bgcolor: "transparent",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottom: "none",
            color: theme.palette.mode === 'dark' ? "#a1a4ab" : "#141b2d",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottomLeftRadius: "16px",
            borderBottomRightRadius: "16px",
          },
          "& .MuiCheckbox-root": {
            color: `${theme.palette.mode === 'dark' ? "#4cceac" : "#3da58a"} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.mode === 'dark' ? "#a1a4ab" : "#727681"} !important`,
            fontWeight: "bold",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
          },
        }}
      >
        <DataGrid
          rows={messages}
          columns={columns}
          checkboxSelection
          onRowClick={(params) => handleRowClick(params.row)}
          disableSelectionOnClick
          onSelectionModelChange={handleRowSelection}
          rowSelectionModel={selectedRows}
          autoHeight
          sx={{ borderRadius: "16px", overflow: "hidden" }}
          components={{
            Toolbar: () => (
              <GridToolbarContainer sx={{ p: 2, borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)", gap: 1 }}>
                {selectedRows.length > 0 && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<ReplayOutlinedIcon />}
                      color="warning"
                      onClick={retrySelected}
                      sx={{ borderRadius: "8px", fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" }, color: "#4cceac", borderColor: "#4cceac" }}
                    >
                      {t("failed.retry")}
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<DeleteOutlinedIcon />}
                      color="error"
                      onClick={handleDeleteConfirmation}
                      sx={{ borderRadius: "8px", fontWeight: "bold", borderWidth: "2px", "&:hover": { borderWidth: "2px" }, color: "#e2726e", borderColor: "#e2726e" }}
                    >
                      {t("failed.delete")}
                    </Button>
                  </>
                )}

                <Box sx={{ flexGrow: 1 }} />

                <GridToolbarFilterButton />
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />
                <GridToolbarExport csvOptions={{ data: handleExport() }} />
              </GridToolbarContainer>
            ),
          }}
        />
      </Box>

      <Message
        open={messageModalOpen}
        onClose={handleCloseMessagesModal}
        message={selectedMessage}
        readOnly={true}
      />

      <ConfirmationModal
        open={showDeleteConfirmation}
        onClose={handleDeleteCancellation}
        onConfirm={handleDelete}
        title={t("failed.confirm_delete")}
        message={t("failed.delete_confirmation_message")}
      />
    </Box>
  );
}
